import { format } from 'date-fns'

/** Keep in sync with `SCORECARD_MIN_DURATION_MINUTES` in `src/shared/meetingScorecardCalculation.ts`. */
export const SCORECARD_MIN_DURATION_MINUTES = 5

const clamp = (value, lower, upper) => Math.max(lower, Math.min(upper, value))

const lerp = (x, x0, y0, x1, y1) => {
  if (x1 === x0) return y1
  const t = (x - x0) / (x1 - x0)
  return y0 + t * (y1 - y0)
}

export const TREND_CHART_RANGE_OPTIONS = [
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
  { id: 'all', label: 'All time' },
]

const TREND_CHART_LABELS = {
  week: 'Last 7 days',
  month: 'Last 30 days',
  year: 'Last 12 months',
  all: 'All time',
}

const FILTER_DAYS_BY_RANGE = {
  week: 7,
  month: 30,
  year: 365,
  all: Infinity,
}

export const isQualifyingMeeting = (meeting) => {
  const totalMeetingMinutes = Number(meeting?.totalMeetingMinutes ?? 0)
  return Number.isFinite(totalMeetingMinutes) && totalMeetingMinutes > SCORECARD_MIN_DURATION_MINUTES
}

/**
 * Time discipline from scheduled wall-clock window vs actual start/end (matches server scorecard).
 * Returns null when timing is missing or invalid — callers should omit from averages, not substitute 50/0.
 */
export const timeDisciplineScoreFromWindow = (meeting) => {
  const server =
    meeting?.timeDisciplineScore != null && Number.isFinite(Number(meeting.timeDisciplineScore))
      ? Math.max(0, Math.min(100, Math.round(Number(meeting.timeDisciplineScore))))
      : null
  if (server != null) return server

  const schedStart =
    meeting?.scheduledStartTime != null ? Date.parse(meeting.scheduledStartTime) : NaN
  const schedEnd =
    meeting?.scheduledEndTime != null ? Date.parse(meeting.scheduledEndTime) : NaN
  const startMs = meeting?.startedAtMs != null ? Number(meeting.startedAtMs) : NaN
  const endMs = meeting?.endedAtMs != null ? Number(meeting.endedAtMs) : NaN
  if (
    !Number.isFinite(schedStart) ||
    !Number.isFinite(schedEnd) ||
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs)
  ) {
    return null
  }
  if (schedEnd <= schedStart || endMs <= startMs) return null

  const scheduledDurationMin = (schedEnd - schedStart) / 60000
  const actualDurationMin = (endMs - startMs) / 60000
  const startLateMinutes = Math.max(0, (startMs - schedStart) / 60000)
  const endLateMinutes = Math.max(0, (endMs - schedEnd) / 60000)
  const durationOverrunMinutes = Math.max(0, actualDurationMin - scheduledDurationMin)
  const violationMinutes = Math.max(
    endLateMinutes,
    durationOverrunMinutes,
    startLateMinutes * 0.35,
  )

  let score
  if (violationMinutes <= 0) score = 100
  else if (violationMinutes <= 1) score = lerp(violationMinutes, 0, 100, 1, 95)
  else if (violationMinutes <= 5) score = lerp(violationMinutes, 1, 95, 5, 75)
  else if (violationMinutes <= 10) score = lerp(violationMinutes, 5, 75, 10, 40)
  else if (violationMinutes <= 15) score = lerp(violationMinutes, 10, 40, 15, 0)
  else score = 0

  return clamp(Math.round(score), 0, 100)
}

/** @deprecated Misnamed — use timeDisciplineScoreFromWindow(meeting). */
export const timeDisciplineScore = timeDisciplineScoreFromWindow

export const interruptionScore = (interruptionCount) => {
  const count = Math.max(0, interruptionCount ?? 0)
  let score
  if (count === 0) score = 100
  else if (count <= 3) score = lerp(count, 0, 100, 3, 85)
  else if (count <= 5) score = lerp(count, 3, 85, 5, 70)
  else if (count <= 10) score = lerp(count, 5, 70, 10, 50)
  else if (count <= 20) score = lerp(count, 10, 50, 20, 20)
  else if (count <= 30) score = lerp(count, 20, 20, 30, 0)
  else score = 0

  return clamp(Math.round(score), 0, 100)
}

export const outputVelocityScore = ({ outputCount, totalMeetingMinutes }) => {
  const rawOutputs = Number.isFinite(outputCount) ? outputCount : 0
  const minutes = Number.isFinite(totalMeetingMinutes) ? totalMeetingMinutes : 0
  if (rawOutputs <= 0 || minutes <= 0) return 0

  const outputsPer60 = rawOutputs * (60 / minutes)
  let score
  if (outputsPer60 <= 0) score = 0
  else if (outputsPer60 <= 1) score = lerp(outputsPer60, 0, 0, 1, 10)
  else if (outputsPer60 <= 5) score = lerp(outputsPer60, 1, 10, 5, 50)
  else if (outputsPer60 <= 10) score = lerp(outputsPer60, 5, 50, 10, 75)
  else if (outputsPer60 <= 15) score = lerp(outputsPer60, 10, 75, 15, 100)
  else score = 100

  return clamp(Math.round(score), 0, 100)
}

export const overallMeetingScore = (meeting) => {
  if (!isQualifyingMeeting(meeting)) return null

  const talkEquity = Math.round((meeting.talkEquityScore ?? 0) * 100)
  const agendaAdherence = Math.round((meeting.agendaAdherence ?? 0) * 100)
  const interruptions = interruptionScore(meeting.interruptionCount)
  const decisionVelocity = outputVelocityScore({
    outputCount: Number(meeting.outputCount ?? meeting.decisionCount ?? 0),
    totalMeetingMinutes: Number(meeting.totalMeetingMinutes ?? 0),
  })
  const timeDiscipline = timeDisciplineScoreFromWindow(meeting)
  const parts = [talkEquity, agendaAdherence, interruptions, decisionVelocity]
  if (timeDiscipline != null) parts.push(timeDiscipline)
  if (parts.length === 0) return null
  return Math.round(parts.reduce((a, b) => a + b, 0) / parts.length)
}

// The aggregate productivity index includes a meeting-volume component that does not map cleanly
// to a single meeting, so the chart focuses on the per-meeting signals that do: decisions and
// interruption control.
export const productivityMeetingScore = (meeting) => {
  if (!isQualifyingMeeting(meeting)) return null

  const decisionScore = Math.min(100, Math.round(Number(meeting.decisionCount ?? 0) * 20))
  const interruptionControl = interruptionScore(meeting.interruptionCount)

  return Math.round((decisionScore + interruptionControl) / 2)
}

export const efficiencyMeetingScore = (meeting) => {
  if (!isQualifyingMeeting(meeting)) return null

  const totalMeetingMinutes = Number(meeting.totalMeetingMinutes ?? 0)
  const onTrackMinutes = Math.max(0, Number(meeting.onTrackMinutes ?? 0))
  const focusScore =
    totalMeetingMinutes > 0
      ? Math.round((Math.min(onTrackMinutes, totalMeetingMinutes) / totalMeetingMinutes) * 100)
      : 0

  const td = timeDisciplineScoreFromWindow(meeting)
  if (td == null) return focusScore
  return Math.round((focusScore + td) / 2)
}

export const agendaQualityMeetingScore = (meeting) => {
  if (!isQualifyingMeeting(meeting)) return null

  const agendaQualityScore = Number(meeting.agendaQualityScore)
  return Number.isFinite(agendaQualityScore) ? clamp(Math.round(agendaQualityScore), 0, 100) : null
}

export const timeSavedMeetingValue = (meeting) => {
  if (!isQualifyingMeeting(meeting)) return null

  const totalTimeSavedMinutes = Number(meeting.totalTimeSavedMinutes)
  return Number.isFinite(totalTimeSavedMinutes) ? Math.round(totalTimeSavedMinutes * 10) / 10 : null
}

const resolveReferenceNowMs = (meetings, referenceNowMs) => {
  if (Number.isFinite(referenceNowMs)) return referenceNowMs
  const latestEndedAtMs = meetings
    .map((meeting) => Number(meeting?.endedAtMs))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => b - a)[0]
  return latestEndedAtMs ?? null
}

export const filterMeetingsForTrendRange = ({ meetings, chartRange, referenceNowMs }) => {
  const filterDays = FILTER_DAYS_BY_RANGE[chartRange] ?? 30
  const referenceNow = resolveReferenceNowMs(meetings, referenceNowMs)

  return meetings.filter((meeting) => {
    const endedAtMs = Number(meeting?.endedAtMs)
    if (!Number.isFinite(endedAtMs)) return false
    if (filterDays === Infinity || referenceNow == null) return true
    return referenceNow - endedAtMs <= filterDays * 24 * 60 * 60 * 1000
  })
}

export const buildDailyOverallScoreTrendSeries = ({ meetings, chartRange, referenceNowMs }) => {
  const filteredMeetings = filterMeetingsForTrendRange({ meetings, chartRange, referenceNowMs })
  if (filteredMeetings.length === 0) {
    return { label: TREND_CHART_LABELS[chartRange] ?? 'Last 30 days', points: [] }
  }

  const meetingsByDay = new Map()
  for (const meeting of filteredMeetings) {
    const endedAtMs = Number(meeting.endedAtMs)
    if (!Number.isFinite(endedAtMs)) continue
    const dayKey = format(new Date(endedAtMs), 'yyyy-MM-dd')
    if (!meetingsByDay.has(dayKey)) meetingsByDay.set(dayKey, [])
    meetingsByDay.get(dayKey).push(meeting)
  }

  const sortedDays = [...meetingsByDay.keys()].sort()
  const points = sortedDays.map((dayKey) => {
    const dayMeetings = meetingsByDay.get(dayKey) ?? []
    const dayScores = dayMeetings
      .map((meeting) => overallMeetingScore(meeting))
      .filter((score) => typeof score === 'number' && score > 0)
    if (dayScores.length === 0) return null
    const score = Math.round(dayScores.reduce((sum, value) => sum + value, 0) / dayScores.length)

    return {
      id: dayKey,
      label: format(new Date(`${dayKey}T12:00:00`), 'MMM d'),
      score,
    }
  }).filter(Boolean)

  return {
    label: TREND_CHART_LABELS[chartRange] ?? 'Last 30 days',
    points,
  }
}

export const buildPerMeetingTrendSeries = ({ meetings, chartRange, referenceNowMs, scoreForMeeting }) => {
  const filteredMeetings = filterMeetingsForTrendRange({ meetings, chartRange, referenceNowMs })
    .slice()
    .sort((left, right) => Number(left.endedAtMs) - Number(right.endedAtMs))

  const points = filteredMeetings
    .map((meeting, index) => {
      const endedAtMs = Number(meeting?.endedAtMs)
      const score = scoreForMeeting(meeting)
      if (!Number.isFinite(endedAtMs) || typeof score !== 'number' || score <= 0) return null

      const mid =
        meeting.meetingId != null && String(meeting.meetingId).trim() !== ''
          ? String(meeting.meetingId).trim()
          : null

      return {
        id: mid ?? `${endedAtMs}-${index}`,
        label: format(new Date(endedAtMs), 'MMM d'),
        score,
        title: meeting.title ?? 'Meeting',
        tooltipDateLabel: format(new Date(endedAtMs), 'MMM d, yyyy'),
        navigateMeetingId: mid,
      }
    })
    .filter(Boolean)

  return {
    label: TREND_CHART_LABELS[chartRange] ?? 'Last 30 days',
    points,
  }
}
